import { Link, TextInput, Tile } from '@Components/common';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styles from './Documents.module.scss';
import classNames from 'classnames';
import { Controller, useFormContext } from 'react-hook-form';
import { DirectFileUpload } from '@Components/common/FileUpload/DirectFileUpload';
import { NumericFormat } from 'react-number-format';
import { OrganizationDocumentTypesEnum } from '@Types/OrganizationDueDiligenceData';
import { PresignedURLTypesEnum } from '@Types/Documents';
import { HelpCenterRoutes } from '@App/paths';

const MAX_FILE_SIZE_IN_MB = 20;
const MAX_ANNUAL_ACCOUNTS_FILES = 5;
const MAX_GOVERNING_DOCUMENTS_FILES = 1;

interface DocumentsProps {
  onGoverningDocsUploadChange?: (isUploading: boolean) => void;
  onAnnualAccountsUploadChange?: (isUploading: boolean) => void;
}

export const Documents = ({
  onGoverningDocsUploadChange,
  onAnnualAccountsUploadChange,
}: DocumentsProps = {}) => {
  const { t } = useTranslation('account');
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Tile
      title={{
        title: t('tabs.documents.add_documents'),
        subtitle: (
          <Trans
            ns="account"
            i18nKey="tabs.documents.add_documents_subtitle"
            components={[<Link href={HelpCenterRoutes.DOCUMENTS} underline />]}
          />
        ),
      }}
      className={classNames(styles.documentsTile)}
    >
      <div className={styles.documentsContainer}>
        <DirectFileUpload
          fieldName="governing_documents"
          config={{
            maxFiles: MAX_GOVERNING_DOCUMENTS_FILES,
            maxFileSizeInMB: MAX_FILE_SIZE_IN_MB,
          }}
          title={t('tabs.documents.governing_document_title')}
          subTitle={t('tabs.documents.governing_document_subtitle')}
          placeholderText={t('tabs.documents.governing_document_upload')}
          required={false}
          presignedUrlType={PresignedURLTypesEnum.ORGANIZATION_KEY_DOCUMENT}
          // Pass in specific field(s) required for OrganizationKeyDocument on the back end.
          additionalFields={{
            document_subtype: OrganizationDocumentTypesEnum.GOVERNING_DOCUMENT,
          }}
          onUploadStateChange={onGoverningDocsUploadChange}
        />

        <DirectFileUpload
          fieldName="annual_accounts"
          config={{
            maxFileSizeInMB: MAX_FILE_SIZE_IN_MB,
            maxFiles: MAX_ANNUAL_ACCOUNTS_FILES,
          }}
          title={t('tabs.documents.annual_accounts_title')}
          subTitle={t('tabs.documents.annual_accounts_subtitle')}
          placeholderText={t('tabs.documents.annual_accounts_upload')}
          required={false}
          presignedUrlType={PresignedURLTypesEnum.ORGANIZATION_KEY_DOCUMENT}
          additionalFields={{
            document_subtype: OrganizationDocumentTypesEnum.ANNUAL_ACCOUNTS,
          }}
          onUploadStateChange={onAnnualAccountsUploadChange}
        />

        <Controller
          name="annual_income"
          control={control}
          render={({ ref, onChange, value }) => (
            <NumericFormat
              customInput={TextInput}
              label={t('tabs.documents.annual_income')}
              placeholder={t('tabs.documents.annual_income_subtitle')}
              name="annual_income"
              type="text"
              inputRef={ref}
              error={!!errors.annual_income}
              helperText={errors.annual_income?.message}
              className={styles.shortField}
              thousandSeparator={','}
              prefix={'£'}
              valueIsNumericString
              value={value}
              onChange={onChange}
            />
          )}
        />
      </div>
    </Tile>
  );
};
